// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Box, Drawer, NavLink, Image, ActionIcon, useMantineColorScheme } from '@mantine/core';
import {
  IconAddressBook,
  IconCalendarEvent,
  IconChartLine,
  IconCircles,
  IconCreditCard,
  IconFileDescription,
  IconInbox,
  IconListCheck,
  IconLogout,
  IconMenu2,
  IconSchool,
  IconSettings,
  IconSwitchHorizontal,
} from '@tabler/icons';
import { FC, useContext } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';
import { AuthContext, useJwtClaims } from '../../Service/Context/AuthContext';
import { useBookitupScope } from '../../Service/Context/BookitupSubscriptionContext';
import { ProfileContext } from '../../Service/Context/ProfileContext';
import { ReactComponent as IconBookitupCircle } from '../../Molecules/bookitup-signet.svg';
import NotificationNavIcon from '../../Molecules/Notifications/NotificationNavIcon';
import { TestS } from '../../TestS';
import { ConstantS } from '../../Service/ConstantS';
import { MixpanelS } from '../../Service/MixpanelS';
import { openForceEventCreationModal } from '../../Molecules/PrimaryNavbar';
import { LicenceS } from '../../Service/LicenceS';
import ThemeSettingsIcon from '../../Atoms/icons/ThemeSettingsIcon';
import { useNavigationItem } from '../../Organismns/Navigation/NavigationItemHook';

const PrimaryHeader: FC = () => {
  const { hasSwitchableProfiles, switchProfile, loadingProfiles, profile } = useContext(ProfileContext);
  const navigate: NavigateFunction = useNavigate();
  const { logout } = useContext(AuthContext);
  const { licence } = useJwtClaims();
  const [opened, { open, close }] = useDisclosure(false);
  const { colorScheme } = useMantineColorScheme();
  const { isScopeSelected: hasSettingsScope } = useBookitupScope('SETTINGS');
  const { isScopeSelected: hasResourceScope } = useBookitupScope('RESOURCE');
  const { isScopeSelected: hasContactsScope } = useBookitupScope('CONTACTS');
  const { isScopeSelected: hasDocumentsScope } = useBookitupScope('DOCUMENTS');
  const { isScopeSelected: hasEmailScope } = useBookitupScope('EMAILS');
  const { isScopeSelected: hasStatisticsScope } = useBookitupScope('STATISTICS');
  const forceEventView = false;
  const settingsPath = hasSettingsScope ? '/settings/userdata' : '/settings/account';
  const eventsActive = useNavigationItem('/');
  const resourcesActive = useNavigationItem('/resources/all');
  const tasksActive = useNavigationItem('/tasks');
  const contactsActive = useNavigationItem('/contacts');
  const postboxActive = useNavigationItem('/postbox');
  const documentsActive = useNavigationItem('/documents');
  const statisticsActive = useNavigationItem('/statistics');

  // eslint-disable-next-line no-underscore-dangle
  const _switchProfile = () => {
    close();
    switchProfile();
  };

  // eslint-disable-next-line no-underscore-dangle
  const _navigate = (pathname: string, page: string) => {
    navigate(pathname);
    MixpanelS.track(ConstantS.TrackingEvents.ViewContent, { page });
  };

  return (
    <Box
      sx={(theme) => ({
        height: 50,
        position: 'relative',
        boxShadow: theme.colorScheme === 'dark' ? 'none' : '0px 0px 8px #0000001A',
      })}
    >
      <Drawer trapFocus={false} withCloseButton={false} opened={opened} onClose={close} size="sm" zIndex={99999}>
        <Box p="sm">
          <Image mb="xl" width={120} src={`/img/bookitup-logo-with-text-${colorScheme}.svg`} />
          <NavLink
            mt="md"
            mb={5}
            label="Events"
            variant="filled"
            active={eventsActive}
            icon={<IconCalendarEvent size={20} />}
            onClick={() => _navigate('/', 'Events')}
            data-testid={TestS.Navigate.TO_EVENTS}
          />
          {profile && hasResourceScope && LicenceS.hasAccessToResourceModule(profile.id) && (
            <NavLink
              mb={5}
              label="Ressourcen"
              variant="filled"icon={<IconCircles size={20} />}
              active={resourcesActive}
              onClick={() => {
                if (forceEventView) {
                  openForceEventCreationModal();
                } else {
                  navigate('/resources/all');
                }
              }}
              data-testid={TestS.Navigate.TO_RESOURCES}
            />
          )}
          <NavLink
            mb={5}
            label="Aufgaben"
            variant="filled"
            icon={<IconListCheck size={20} />}
            active={tasksActive}
            onClick={() => {
              if (forceEventView) {
                openForceEventCreationModal();
              } else {
                _navigate('/tasks', 'Tasks');
              }
            }}
            data-testid={TestS.Navigate.TO_TASKS}
          />
          {hasContactsScope && (
            <NavLink
              mb={5}
              label="Kontakte"
              variant="filled"icon={<IconAddressBook size={20} />}
              active={contactsActive}
              onClick={() => {
                if (forceEventView) {
                  openForceEventCreationModal();
                } else {
                  _navigate('/contacts', 'Contacts');
                }
              }}
              data-testid={TestS.Navigate.TO_CONTACTS}
            />
          )}
          {hasEmailScope && (
            <NavLink
              mb={5}
              label="Postfach"variant="filled"
              icon={<IconInbox size={20} />}
              active={postboxActive}
              onClick={() => {
                if (forceEventView) {
                  openForceEventCreationModal();
                } else {
                  _navigate('/postbox/inbox', 'Inbox');
                }
              }}
              data-testid={TestS.Navigate.TO_MAILS}
            />
          )}
          {hasDocumentsScope && (
            <NavLink
              mb={5}
              label="Dokumente"variant="filled"
              active={documentsActive}
              icon={<IconFileDescription size={20} />}
              onClick={() => {
                if (forceEventView) {
                  openForceEventCreationModal();
                } else {
                  _navigate('/documents', 'Documents');
                }
              }}
              data-testid={TestS.Navigate.TO_DOCUMENTS}
            />
          )}
          {hasStatisticsScope && (
            <NavLink
              mb={5}
              label="Statistiken"variant="filled"
              active={statisticsActive}
              icon={<IconChartLine size={20} />}
              onClick={() => {
                if (forceEventView) {
                  openForceEventCreationModal();
                } else {
                  _navigate(`/statistics?year=${new Date().getFullYear()}`, 'Statistics');
                }
              }}
              data-testid={TestS.Navigate.TO_STATISTICS}
            />
          )}
        </Box>
        <Box
          p="sm"
          pb={40}
          pos="absolute"
          sx={(theme) => ({
            bottom: 0,
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
            width: '100%',
          })}
        >
          <NavLink
            mb={5}
            label="Einstellungen"
            icon={<IconSettings size={20} />}
            onClick={() => {
              if (forceEventView) {
                openForceEventCreationModal();
              } else {
                close();
                _navigate(settingsPath, 'Settings');
              }
            }}
            data-testid={TestS.Navigate.TO_SETTINGS}
          />
          {hasSettingsScope && LicenceS.inTrialPeriod(licence) && (
            <NavLink
              mb={5}
              label="Paket & Abrechnung"
              icon={<IconCreditCard size={20} />}
              onClick={() => {
                close();
                navigate('/settings/plans');
              }}
            />
          )}
          {!loadingProfiles && hasSwitchableProfiles && (
            <NavLink
              mb={5}
              label="Profil wechseln"
              icon={<IconSwitchHorizontal size={20} />}
              onClick={_switchProfile}
            />
          )}
          <NavLink
            mb={5}
            label="Akademie"
            icon={<IconSchool size={20} />}
            onClick={() => navigate('/academy/welcome')}
          />
          <NavLink
            label="Abmelden"
            mb={5}
            icon={<IconLogout size={20} />}
            onClick={() => {
              MixpanelS.track(ConstantS.TrackingEvents.Logout);
              logout();
            }}
          />
        </Box>
      </Drawer>
      <ActionIcon
        sx={(theme) => ({
          position: 'absolute',
          left: 15,
          top: 10,
          color: theme.colorScheme === 'dark' ? '#E5E5E5' : '#1A1A1A',
        })}
      >
        <IconMenu2 onClick={open} />
      </ActionIcon>
      <Box sx={{ position: 'absolute', right: 45, top: 5 }}>
        <ThemeSettingsIcon />
      </Box>
      <Box sx={{ position: 'absolute', right: 5, top: 5 }}>
        <NotificationNavIcon />
      </Box>
      <Box sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}>
        <IconBookitupCircle height={32} width={32} />
      </Box>
    </Box>
  );
};

export default PrimaryHeader;
